/**
 * A fila de envios do Desk Cloud (docs/spec/desk-cloud.md, "Envios"): cada arquivo com o progresso, o
 * fim ("Em Macros e Testes" ou "Enviado") e o erro. O que o plano recusa diz o tamanho, o limite e o
 * plano, com "Ver planos com arquivos até Z". Cada envio tem o menu do botão direito.
 */
import { useState } from 'react';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import { usePlanGrid, usePlanName } from '../plans/usePlanName.js';
import { firstPlanFor } from '../plans/planModel.js';
import { inferKind } from '../shared.js';
import { useLoggedNav } from '../states/nav.js';
import { useApp } from '../store.js';
import { Icon } from '../ui/icons.js';
import { useLongPress } from '../ui/touch.js';
import { cancelUpload, retryUpload } from './cloudActions.js';
import { uploadMenu } from './cloudMenus.js';
import { KIND_COLORS, plural } from './cloudModel.js';
import { useCloud, type CloudUpload } from './cloudStore.js';
import { useCloudUi } from './cloudUi.js';

export function UploadTray() {
  const t = useT();
  const uploads = useCloud((s) => s.uploads);
  const [open, setOpen] = useState(true);
  if (!uploads.length) return null;
  const sending = uploads.filter((u) => u.status === 'waiting' || u.status === 'sending').length;
  const done = uploads.filter((u) => u.status === 'done').length;
  const failed = uploads.filter((u) => u.status === 'error').length;
  const title = sending
    ? plural(t, 'cloud.uploads.sending', sending)
    : [plural(t, 'cloud.uploads.done', done), ...(failed ? [plural(t, 'cloud.uploads.failed', failed)] : [])].join(' · ');
  return (
    <section className="rd-cloudTray" aria-label={t('cloud.uploads')} aria-live="polite">
      <div className="rd-cloudTray__head">
        <span>{title}</span>
        <button type="button" className="rd-iconBtn" aria-label={t(open ? 'cloud.uploads.collapse' : 'cloud.uploads.expand')} aria-expanded={open} onClick={() => setOpen(!open)}>
          <Icon name={open ? 'chevronDown' : 'chevronUp'} size={16} />
        </button>
        {sending ? null : (
          <button type="button" className="rd-iconBtn" aria-label={t('cloud.uploads.close')} onClick={() => useCloud.getState().clearUploads()}>
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
      {open ? (
        <ul className="rd-cloudTray__list">
          {uploads.map((upload) => (
            <UploadRow key={upload.id} upload={upload} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function UploadRow({ upload }: { upload: CloudUpload }) {
  const t = useT();
  const ui = useCloudUi();
  const press = useLongPress();
  const plan = useApp((s) => s.plan);
  const grid = usePlanGrid();
  const ceilings = useApp((s) => s.planCeilings);
  const planName = usePlanName();
  const go = useLoggedNav((s) => s.go);
  const kind = upload.stored?.kind ?? inferKind({ mime: upload.file.type, name: upload.file.name });
  // o primeiro plano que recebe um arquivo deste tamanho, com o teto do servidor (para o botão de ver planos)
  const fits = firstPlanFor(upload.file.size, grid, ceilings);
  const plans = () => go({ kind: 'settings', page: 'plan' });
  const reveal = () => {
    if (upload.stored) {
      useCloud.getState().go(upload.roomId ? { view: 'room', roomId: upload.roomId } : upload.folderId ? { view: 'folder', folderId: upload.folderId } : { view: 'all' });
      useCloud.getState().setFocus(upload.stored.id);
    }
  };
  const status =
    upload.status === 'sending'
      ? `${Math.round(upload.progress * 100)}%`
      : upload.status === 'waiting'
        ? t('cloud.uploads.waiting')
        : upload.status === 'done'
          ? upload.roomId
            ? t('cloud.uploads.sentTo', { room: ui.roomName(upload.roomId) })
            : t('cloud.uploads.sent')
          : t('cloud.uploads.error');
  return (
    <li
      className={`rd-cloudTray__item is-${upload.status}`}
      {...press.bind((anchor) =>
        ui.openMenu(anchor, uploadMenu(ui.menuContext, { status: upload.status, planLimited: !!upload.planLimited }, { cancel: () => cancelUpload(upload.id), retry: () => retryUpload(upload.id), reveal, plans }), upload.file.name),
      )}
    >
      <div className="rd-cloudTray__line">
        <span className="rd-cloudDot" style={{ background: KIND_COLORS[kind] }} aria-hidden="true" />
        <span className="rd-cloudTray__name" title={upload.file.name}>
          {upload.file.name}
        </span>
        <span className="rd-cloudTray__status">
          {upload.status === 'done' ? <Icon name="check" size={14} /> : upload.status === 'error' ? <Icon name="close" size={14} /> : null}
          {status}
        </span>
        {upload.status === 'waiting' || upload.status === 'sending' ? (
          <button type="button" className="rd-iconBtn" aria-label={t('cloud.uploads.cancel')} title={t('cloud.uploads.cancel')} onClick={() => cancelUpload(upload.id)}>
            <Icon name="close" size={13} />
          </button>
        ) : null}
      </div>
      {upload.status === 'sending' ? (
        <div className="rd-cloudTray__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(upload.progress * 100)} aria-label={t('cloud.uploads.progress', { name: upload.file.name })}>
          <span style={{ width: `${upload.progress * 100}%` }} />
        </div>
      ) : null}
      {upload.status === 'error' ? (
        <div className="rd-cloudTray__error">
          {upload.planLimited === 'too-large' && plan
            ? t('cloud.uploads.tooLarge', { size: formatBytes(upload.file.size), limit: formatBytes(plan.entitlements.maxFileBytes), plan: planName(plan.id) })
            : upload.planLimited === 'no-space'
              ? t('cloud.uploads.noSpace')
              : upload.error}
          {upload.planLimited ? (
            <button type="button" className="rd-cloudLink" onClick={plans}>
              {upload.planLimited === 'too-large' && fits ? t('cloud.uploads.plansWith', { size: formatBytes(fits.maxFileBytes) }) : t('cloud.uploads.plans')}
            </button>
          ) : (
            <button type="button" className="rd-cloudLink" onClick={() => retryUpload(upload.id)}>
              {t('cloud.uploads.retry')}
            </button>
          )}
        </div>
      ) : null}
    </li>
  );
}
